import React from 'react';
import { LoadingSpinner } from './LoadingSpinner';
import { focusRingCls } from './styles';

/**
 * `bare` is for clickables that are not button-shaped — a selectable card, a
 * list row, a tab with a count — where the caller owns layout and colour and
 * the primitive contributes only the focus ring, the disabled state and the
 * transition. Every other variant is a fixed-height control.
 */
export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'bare';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  /** 28px / 32px / 36px tall. */
  size?: ButtonSize;
  /** Replaces the icon with an inline spinner and disables the button. */
  loading?: boolean;
  /** Leading icon. With no children the button renders as a square icon button; pass an aria-label. */
  icon?: React.ReactNode;
  fullWidth?: boolean;
}

const common = `disabled:opacity-50 disabled:cursor-not-allowed ${focusRingCls}`;
const base = `inline-flex items-center justify-center whitespace-nowrap rounded-lg font-medium transition-colors duration-150 ${common}`;
// transition-all rather than transition-colors: a bare caller may animate an
// offset (the sidebar handle), and transition-colors would win the cascade.
const bare = `transition-all duration-150 ${common}`;

const sizes: Record<ButtonSize, { shape: string; padded: string; square: string }> = {
  sm: { shape: 'h-7 text-sm gap-1.5', padded: 'px-2.5', square: 'w-7' },
  md: { shape: 'h-8 text-sm gap-2', padded: 'px-3', square: 'w-8' },
  lg: { shape: 'h-9 text-base gap-2', padded: 'px-4', square: 'w-9' },
};

const variants: Record<ButtonVariant, string> = {
  primary: 'bg-vein-red-500 text-white hover:bg-vein-red-600 active:bg-vein-red-700',
  secondary: 'bg-white text-gray-800 border border-vein-border hover:bg-gray-50 active:bg-gray-100',
  ghost: 'text-gray-700 hover:bg-gray-100 active:bg-gray-200',
  danger: 'bg-white text-red-600 border border-red-200 hover:bg-red-50 active:bg-red-100',
  bare: '',
};

// type defaults to "button" so a Button inside a form never submits it by
// accident; pass type="submit" explicitly on the one that should.
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    icon,
    fullWidth = false,
    className = '',
    disabled,
    type = 'button',
    children,
    ...rest
  },
  ref,
) {
  const s = sizes[size];
  const iconOnly = children === undefined || children === null;
  const shape =
    variant === 'bare'
      ? bare
      : `${base} ${s.shape} ${iconOnly ? s.square : s.padded} ${variants[variant]}`;
  const leading = loading ? (
    <LoadingSpinner inline size="xs" tone={variant === 'primary' ? 'white' : 'neutral'} />
  ) : icon ? (
    <span aria-hidden="true" className="inline-flex shrink-0">
      {icon}
    </span>
  ) : null;

  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={`${shape} ${fullWidth ? 'w-full' : ''} ${className}`}
      {...rest}
    >
      {leading}
      {children}
    </button>
  );
});

export default Button;
